// Terms of Use for Sign This!
// Last updated: 2026-02-05

export const TERMS_LAST_UPDATED = '2026-02-05';

export const TERMS_OF_USE = {
  en: `
# Terms of Use
**Last updated: February 5, 2026**

Thank you for using Sign This! (the "Service"), a platform designed to facilitate the collection of digital signatures on documents. The Service is provided by Loxia Labs LLC, a company registered in the United States ("Company", "We," "Us," or "Our"). These Terms of Use ("Terms") govern your access to and use of the Service and related websites, dashboards, and services provided by the Company.

## 1. DEFINITIONS

- **"User" or "You"** – each person or entity accessing or using the Service, from any type of device.
- **"Publisher"** – a User who uploads documents and creates signature collection forms.
- **"Signer"** – a person who signs a document through the Service.
- **"Document"** – any PDF or image file uploaded to the Service for signature collection.

## 2. ACCEPTANCE OF TERMS

- The use of the Service is subject to these Terms of Use and to our Privacy Policy, which forms an integral part thereof. By accessing and/or using the Service and any of its contents, you indicate that you have read and understood these Terms of Use and that you accept and consent to its contents. **IF YOU DO NOT AGREE TO THESE TERMS OF USE, DO NOT USE THE SERVICE.**

- These Terms of Use form a legally binding contract between you or anyone using the Service on your behalf and the Company. Any use of the Service is subject to these Terms of Use and all applicable laws, rules, and regulations in the country in which the Service is being used.

- You represent and warrant that you have the power and legal authority to accept these Terms, and that your use of the Service will comply with all applicable laws (including privacy, data protection, and electronic signature laws). You shall use the Service solely for lawful purposes and in accordance with these Terms.

## 3. DESCRIPTION OF SERVICE

- The Service provides a platform for Publishers to upload documents, define signature fields, and collect digital signatures from Signers.

- The Service includes:
  - Document upload and storage
  - Signature collection form creation
  - Digital signature capture
  - Signature data management and export
  - Link generation for signature collection

- **IMPORTANT LEGAL NOTICE:** The digital signatures collected through this Service are intended for record-keeping and informal agreement purposes. The Company makes no representations regarding the legal validity or enforceability of signatures collected through the Service in any jurisdiction. Publishers are solely responsible for determining whether the signatures collected meet their legal requirements.

## 4. USER RESPONSIBILITIES

### 4.1 Publisher Responsibilities

As a Publisher, you are responsible for:

- Ensuring you have the legal right to collect signatures on the documents you upload
- Obtaining proper consent from Signers before collecting their signatures and personal information
- Complying with all applicable privacy laws and regulations regarding the collection and processing of Signer data
- Maintaining the confidentiality of your account password
- Using the exported signature data in compliance with applicable laws
- Not uploading documents that contain illegal, harmful, or offensive content

### 4.2 General User Responsibilities

All Users agree to:

- Provide accurate and complete information when using the Service
- Not use the Service for any fraudulent, illegal, or unauthorized purpose
- Not attempt to circumvent security measures or access unauthorized data
- Not use the Service to collect signatures for illegal contracts or agreements
- Not reverse engineer, decompile, or attempt to extract the source code of the Service

## 5. DATA SECURITY AND ENCRYPTION

- All personal data collected through signature forms is encrypted using industry-standard cryptographic methods.
- Only the Publisher with the correct password can decrypt and access the signature data.
- The Company does not have access to decrypted signature data or Publisher passwords.
- While we implement robust security measures, no system is completely secure. Users acknowledge and accept the inherent risks of transmitting data over the internet.

## 6. INTELLECTUAL PROPERTY

- All rights in the Service, including intellectual property rights, copyrights, trademarks, and trade secrets, are exclusively owned by the Company.
- Publishers retain ownership of their uploaded documents and collected signature data.
- You are granted a limited, non-exclusive license to use the Service for its intended purpose, subject to these Terms.

## 7. DISCLAIMER OF WARRANTIES

**THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED.**

The Company disclaims all warranties, including but not limited to:

- Merchantability and fitness for a particular purpose
- Non-infringement of third-party rights
- Accuracy, reliability, or completeness of the Service
- Uninterrupted or error-free operation
- Legal validity or enforceability of collected signatures

**THE COMPANY DOES NOT GUARANTEE THAT SIGNATURES COLLECTED THROUGH THE SERVICE WILL BE LEGALLY BINDING OR ENFORCEABLE IN ANY JURISDICTION.**

## 8. LIMITATION OF LIABILITY

To the fullest extent permitted by law:

- The Company shall not be liable for any indirect, special, incidental, punitive, exemplary, or consequential damages arising out of or related to these Terms or the Service.
- The Company's total cumulative liability for all claims shall not exceed the amount paid by you for the Service in the twelve (12) months preceding the claim, or $100 USD, whichever is greater.
- The Company shall not be liable for any loss of data, documents, or signatures, regardless of cause.

## 9. INDEMNIFICATION

You agree to indemnify, defend, and hold harmless the Company, its affiliates, officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, and expenses arising out of:

- Your use of the Service in violation of these Terms or applicable law
- Documents you upload or signatures you collect through the Service
- Any dispute between you and any Signer
- Your violation of any third-party rights

## 10. PRIVACY

Your use of the Service is also governed by our Privacy Policy, which describes how we collect, use, and protect your personal information. By using the Service, you consent to the practices described in the Privacy Policy.

## 11. MODIFICATIONS TO SERVICE AND TERMS

- The Company may modify, suspend, or discontinue the Service at any time with reasonable notice.
- These Terms may be amended at any time. Changes become effective when published. Your continued use of the Service after changes are published constitutes acceptance of the modified Terms.

## 12. TERMINATION

- You may stop using the Service at any time.
- The Company may terminate or suspend your access to the Service at any time for violation of these Terms or for any other reason at its sole discretion.
- Upon termination, your right to use the Service ceases immediately. The Company may delete your data in accordance with its data retention policies.

## 13. GOVERNING LAW AND JURISDICTION

These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to conflict of laws principles. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the state and federal courts located in Delaware, United States.

## 14. CONTACT INFORMATION

If you have questions or concerns regarding these Terms of Use, please contact us at: contact@loxia.ai
`,

  he: `
# תנאי שימוש
**עודכן לאחרונה: 5 בפברואר 2026**

תודה שבחרתם להשתמש בSign This! ("השירות"), פלטפורמה המיועדת לאיסוף חתימות דיגיטליות על מסמכים. השירות מסופק על ידי Loxia Labs LLC, חברה רשומה בארצות הברית ("החברה", "אנחנו" או "שלנו"). תנאי שימוש אלה ("התנאים") מסדירים את הגישה שלכם לשירות ואת השימוש בו.

## 1. הגדרות

- **"משתמש" או "אתה"** – כל אדם או ישות הניגשים לשירות או משתמשים בו, מכל סוג של מכשיר.
- **"מפרסם"** – משתמש המעלה מסמכים ויוצר טפסים לאיסוף חתימות.
- **"חותם"** – אדם החותם על מסמך דרך השירות.
- **"מסמך"** – כל קובץ PDF או תמונה שהועלה לשירות לצורך איסוף חתימות.

## 2. קבלת התנאים

- השימוש בשירות כפוף לתנאי שימוש אלה ולמדיניות הפרטיות שלנו, המהווה חלק בלתי נפרד מהם. על ידי גישה ו/או שימוש בשירות ובכל תוכנו, אתם מציינים שקראתם והבנתם תנאי שימוש אלה ושאתם מקבלים ומסכימים לתוכנם. **אם אינכם מסכימים לתנאי שימוש אלה, אל תשתמשו בשירות.**

- תנאי שימוש אלה מהווים חוזה מחייב משפטית בינכם או בין כל מי שמשתמש בשירות בשמכם לבין החברה. כל שימוש בשירות כפוף לתנאי שימוש אלה ולכל החוקים, הכללים והתקנות החלים במדינה בה נעשה שימוש בשירות.

- אתם מצהירים ומתחייבים שיש לכם את הסמכות החוקית לקבל תנאים אלה, ושהשימוש שלכם בשירות יעמוד בכל החוקים החלים (כולל חוקי פרטיות, הגנת מידע וחתימות אלקטרוניות).

## 3. תיאור השירות

- השירות מספק פלטפורמה למפרסמים להעלות מסמכים, להגדיר שדות חתימה ולאסוף חתימות דיגיטליות מחותמים.

- השירות כולל:
  - העלאת מסמכים ואחסונם
  - יצירת טפסים לאיסוף חתימות
  - לכידת חתימות דיגיטליות
  - ניהול נתוני חתימות וייצואם
  - יצירת קישורים לאיסוף חתימות

- **הודעה משפטית חשובה:** החתימות הדיגיטליות שנאספות דרך שירות זה מיועדות למטרות תיעוד והסכמה בלתי פורמלית. החברה אינה מייצגת דבר לגבי התוקף המשפטי או האכיפות של חתימות שנאספו דרך השירות בכל תחום שיפוט. המפרסמים אחראים באופן בלעדי לקביעה האם החתימות שנאספו עומדות בדרישות המשפטיות שלהם.

## 4. אחריות המשתמש

### 4.1 אחריות המפרסם

כמפרסם, אתם אחראים על:

- הבטחה שיש לכם זכות חוקית לאסוף חתימות על המסמכים שאתם מעלים
- קבלת הסכמה נאותה מחותמים לפני איסוף חתימותיהם ומידע אישי עליהם
- ציות לכל חוקי הפרטיות והתקנות החלות על איסוף ועיבוד נתוני חותמים
- שמירה על סודיות סיסמת החשבון שלכם
- שימוש בנתוני החתימות המיוצאים בהתאם לחוקים החלים
- אי העלאת מסמכים המכילים תוכן בלתי חוקי, מזיק או פוגעני

### 4.2 אחריות כללית של משתמשים

כל המשתמשים מסכימים:

- לספק מידע מדויק ומלא בעת שימוש בשירות
- לא להשתמש בשירות לכל מטרה הונאתית, בלתי חוקית או בלתי מורשית
- לא לנסות לעקוף אמצעי אבטחה או לגשת למידע בלתי מורשה
- לא להשתמש בשירות לאיסוף חתימות על חוזים או הסכמים בלתי חוקיים
- לא לבצע הנדסה לאחור, לפרק או לנסות לחלץ את קוד המקור של השירות

## 5. אבטחת מידע והצפנה

- כל המידע האישי שנאסף דרך טופסי החתימה מוצפן באמצעות שיטות קריפטוגרפיות תקניות.
- רק המפרסם עם הסיסמה הנכונה יכול לפענח ולגשת לנתוני החתימות.
- לחברה אין גישה לנתוני חתימות מפוענחים או לסיסמאות מפרסמים.
- למרות שאנו מיישמים אמצעי אבטחה חזקים, אין מערכת מאובטחת לחלוטין. המשתמשים מכירים ומקבלים את הסיכונים הטבועים בהעברת מידע דרך האינטרנט.

## 6. קניין רוחני

- כל הזכויות בשירות, כולל זכויות קניין רוחני, זכויות יוצרים, סימני מסחר וסודות מסחריים, שייכות באופן בלעדי לחברה.
- מפרסמים שומרים על בעלותם במסמכים שהעלו ובנתוני החתימות שנאספו.
- ניתן לכם רישיון מוגבל ולא בלעדי להשתמש בשירות למטרתו המיועדת, בכפוף לתנאים אלה.

## 7. הסרת אחריות מאחריות

**השירות מסופק על בסיס "כמות שהוא" ו"כפי שזמין", ללא אחריות מכל סוג שהוא, בין אם מפורשת או משתמעת.**

החברה מסירה כל אחריות, כולל אך לא מוגבלת ל:

- סחירות והתאמה למטרה מסוימת
- אי-הפרה של זכויות צד שלישי
- דיוק, אמינות או שלמות השירות
- פעולה ללא הפרעות או ללא שגיאות
- תוקף משפטי או אכיפות של חתימות שנאספו

**החברה אינה מבטיחה שחתימות שנאספו דרך השירות יהיו מחייבות משפטית או ניתנות לאכיפה בכל תחום שיפוט.**

## 8. הגבלת אחריות

במידה המרבית המותרת על פי חוק:

- החברה לא תהיה אחראית לכל נזק עקיף, מיוחד, מקרי, עונשי, לדוגמה או תוצאתי הנובע מתנאים אלה או מהשירות.
- האחריות הכוללת המצטברת של החברה לכל התביעות לא תעלה על הסכום ששולם על ידכם עבור השירות בשנים עשר (12) החודשים שקדמו לתביעה, או 100 דולר ארה"ב, הגבוה מביניהם.
- החברה לא תהיה אחראית לכל אובדן של נתונים, מסמכים או חתימות, ללא קשר לסיבה.

## 9. שיפוי

אתם מסכימים לשפות, להגן ולהחזיק את החברה, שותפיה, נושאי המשרה, הדירקטורים, העובדים והסוכנים שלה נקיים מכל תביעות, נזקים, הפסדים, התחייבויות, עלויות והוצאות הנובעים מ:

- השימוש שלכם בשירות בניגוד לתנאים אלה או לחוק החל
- מסמכים שאתם מעלים או חתימות שאתם אוספים דרך השירות
- כל מחלוקת בינכם לבין כל חותם
- הפרה שלכם של זכויות צד שלישי

## 10. פרטיות

השימוש שלכם בשירות כפוף גם למדיניות הפרטיות שלנו, המתארת כיצד אנו אוספים, משתמשים ומגנים על המידע האישי שלכם. על ידי שימוש בשירות, אתם מסכימים לנהלים המתוארים במדיניות הפרטיות.

## 11. שינויים בשירות ובתנאים

- החברה רשאית לשנות, להשעות או להפסיק את השירות בכל עת עם הודעה סבירה.
- ניתן לתקן תנאים אלה בכל עת. השינויים נכנסים לתוקף עם פרסומם. המשך השימוש שלכם בשירות לאחר פרסום השינויים מהווה קבלה של התנאים המתוקנים.

## 12. סיום

- אתם רשאים להפסיק להשתמש בשירות בכל עת.
- החברה רשאית לסיים או להשעות את הגישה שלכם לשירות בכל עת בגין הפרה של תנאים אלה או מכל סיבה אחרת לפי שיקול דעתה הבלעדי.
- עם הסיום, זכותכם להשתמש בשירות מסתיימת מיד. החברה רשאית למחוק את הנתונים שלכם בהתאם למדיניות שמירת הנתונים שלה.

## 13. דין חל וסמכות שיפוט

תנאים אלה יהיו כפופים לחוקי מדינת דלאוור, ארצות הברית, ויפורשו בהתאם להם, ללא התחשבות בעקרונות ברירת הדין. כל מחלוקת הנובעת מתנאים אלה תהיה כפופה לסמכות השיפוט הבלעדית של בתי המשפט המדינתיים והפדרליים בדלאוור, ארצות הברית.

## 14. פרטי התקשרות

אם יש לכם שאלות או חששות לגבי תנאי שימוש אלה, אנא צרו קשר בכתובת: contact@loxia.ai
`
};
